import { useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { pageToPath, parseApplicationReviewLocation, pathToPage, type NavOpts } from '../routes'
import { NavigationContext, type NavigationContextValue } from './navigationContextValue'

const APPLICATION_REVIEW_FROM_RECIPIENT = {
  parentCrumb: { label: '受給者／申出書 検索', page: 'recipient-search' },
  pageTitle: '受給者詳細情報',
}
const APPLICATION_REVIEW_FROM_OCR_LIST = {
  parentCrumb: { label: 'データ取込', page: 'ocr-read-list' },
  pageTitle: 'OCR読取結果確認',
}

export function NavigationProvider({ children }: { children: ReactNode }) {
  const routerNavigate = useNavigate()
  const location = useLocation()
  const currentPage = pathToPage(location.pathname)

  const [initialReviewSnapshot] = useState(() =>
    parseApplicationReviewLocation(location.pathname, location.search)
  )
  const initialReviewFromRecipient = initialReviewSnapshot.reviewParentPage === 'recipient-search'

  const [ledgerReviewCaseIndex, setLedgerReviewCaseIndex] = useState(initialReviewSnapshot.caseIndex ?? 0)
  const [ledgerReviewImportBatchId, setLedgerReviewImportBatchId] = useState<number | null>(initialReviewSnapshot.importBatchId ?? null)
  const [ledgerReviewOcrCaseId, setLedgerReviewOcrCaseId] = useState<number | null>(initialReviewSnapshot.ocrCaseId ?? null)
  const [recipientBaseCrumb, setRecipientBaseCrumb] = useState({ label: '受給者／申出書 検索', page: 'recipient-search' })
  const [pendingAlert, setPendingAlert] = useState<string | null>(null)
  const [applicationReviewParentCrumb, setApplicationReviewParentCrumb] = useState(() =>
    initialReviewFromRecipient ? APPLICATION_REVIEW_FROM_RECIPIENT.parentCrumb : APPLICATION_REVIEW_FROM_OCR_LIST.parentCrumb
  )
  const [applicationReviewPageTitle, setApplicationReviewPageTitle] = useState(() =>
    initialReviewFromRecipient ? APPLICATION_REVIEW_FROM_RECIPIENT.pageTitle : APPLICATION_REVIEW_FROM_OCR_LIST.pageTitle
  )
  const [recipientDetailPageTitle, setRecipientDetailPageTitle] = useState('受給者詳細情報')
  const [appDocEditReturnPage, setAppDocEditReturnPage] = useState('application-doc-detail')
  const [notification, setNotification] = useState<string | null>(null)

  const showNotification = useCallback((msg: string) => {
    setNotification(msg)
    setTimeout(() => setNotification(null), 3000)
  }, [])

  const lastSyncedReviewPathRef = useRef<string>(`${location.pathname}${location.search}`)
  useEffect(() => {
    const key = `${location.pathname}${location.search}`
    if (lastSyncedReviewPathRef.current === key) return
    lastSyncedReviewPathRef.current = key
    if (currentPage !== 'application-review') return
    const parsed = parseApplicationReviewLocation(location.pathname, location.search)
    // URL から審査画面のナビ状態を同期する（path 変化時の一度きり、ガードで多重実行防止）。
    /* eslint-disable react-hooks/set-state-in-effect */
    setLedgerReviewImportBatchId(parsed.importBatchId ?? null)
    setLedgerReviewOcrCaseId(parsed.ocrCaseId ?? null)
    setLedgerReviewCaseIndex(parsed.caseIndex ?? 0)
    if (parsed.reviewParentPage === 'recipient-search') {
      setApplicationReviewParentCrumb(APPLICATION_REVIEW_FROM_RECIPIENT.parentCrumb)
      setApplicationReviewPageTitle(APPLICATION_REVIEW_FROM_RECIPIENT.pageTitle)
    } else {
      setApplicationReviewParentCrumb(APPLICATION_REVIEW_FROM_OCR_LIST.parentCrumb)
      setApplicationReviewPageTitle(APPLICATION_REVIEW_FROM_OCR_LIST.pageTitle)
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [currentPage, location.pathname, location.search])

  useEffect(() => {
    if (!pendingAlert) return
    if (currentPage === 'recipient-detail' || currentPage === 'application-review') {
      const msg = pendingAlert
      setTimeout(() => {
        setPendingAlert(null)
        window.dispatchEvent(new CustomEvent('app:pending-alert', { detail: { message: msg } }))
      }, 0)
    }
  }, [currentPage, pendingAlert])

  const navigate = useCallback((target: string, opts?: NavOpts) => {
    if (target === 'application-review') {
      if (opts && typeof opts.caseIndex === 'number') {
        setLedgerReviewCaseIndex(opts.caseIndex)
      }
      if (opts && typeof opts.importBatchId === 'number') {
        setLedgerReviewImportBatchId(opts.importBatchId)
      } else {
        setLedgerReviewImportBatchId(null)
      }
      if (opts && typeof opts.ocrCaseId === 'number') {
        setLedgerReviewOcrCaseId(opts.ocrCaseId)
      } else {
        setLedgerReviewOcrCaseId(null)
      }
      if (opts && opts.reviewParentPage === 'recipient-search') {
        setApplicationReviewParentCrumb({ label: '受給者／申出書 検索', page: 'recipient-search' })
        setApplicationReviewPageTitle('受給者詳細情報')
        setPendingAlert('要注意対応者です！！内容を確認してから対応してください！')
      } else {
        setApplicationReviewParentCrumb({ label: 'データ取込', page: 'ocr-read-list' })
        setApplicationReviewPageTitle('OCR読取結果確認')
      }
    }
    if (target === 'recipient-detail') {
      if (opts && opts.baseCrumb) setRecipientBaseCrumb(opts.baseCrumb)
      if (opts && typeof opts.detailPageTitle === 'string') setRecipientDetailPageTitle(opts.detailPageTitle)
    }
    if (target === 'recipient-edit') {
      if (opts && opts.baseCrumb) setRecipientBaseCrumb(opts.baseCrumb)
      if (opts && typeof opts.detailPageTitle === 'string') setRecipientDetailPageTitle(opts.detailPageTitle)
    }
    if (target === 'recipient-new') {
      if (opts && opts.baseCrumb) setRecipientBaseCrumb(opts.baseCrumb)
    }
    if (opts && typeof opts.alert === 'string') {
      setPendingAlert(opts.alert)
    }
    if (target === 'application-doc-edit' || target === 'application-document-edit') {
      setAppDocEditReturnPage(opts && opts.returnPage ? opts.returnPage : 'application-doc-detail')
    }
    const path = pageToPath(target, opts)
    if (opts?.resetSearchPersistence) {
      routerNavigate(path, { state: { resetSearchPersistence: true } })
    } else {
      routerNavigate(path)
    }
    window.scrollTo(0, 0)
  }, [routerNavigate])

  const value = useMemo<NavigationContextValue>(
    () => ({
      ledgerReviewCaseIndex,
      ledgerReviewImportBatchId,
      ledgerReviewOcrCaseId,
      recipientBaseCrumb,
      pendingAlert,
      applicationReviewParentCrumb,
      applicationReviewPageTitle,
      recipientDetailPageTitle,
      appDocEditReturnPage,
      notification,
      navigate,
      showNotification,
      setPendingAlert,
      currentPage,
    }),
    [
      ledgerReviewCaseIndex,
      ledgerReviewImportBatchId,
      ledgerReviewOcrCaseId,
      recipientBaseCrumb,
      pendingAlert,
      applicationReviewParentCrumb,
      applicationReviewPageTitle,
      recipientDetailPageTitle,
      appDocEditReturnPage,
      notification,
      navigate,
      showNotification,
      currentPage,
    ],
  )

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  )
}
